import { DataLayerAction } from '@corratech/pylot-tag-manager'
import { resolveGtmBootstrap } from './utils'
import { getApplyMapping } from './mapping/getApplyMapping'
import { GTMActionOverride, GTMEventDataMapping } from './GTM.d'
import { useDataLayerData } from './hooks/useDataLayerData'
import { useEventQueue } from './hooks/useEventQueue'
import './utils/helper/data-layer-helper'
import { getEventDataConsumer } from './utils/getEventDataConsumer'

declare global {
    interface Window {
        dataLayer?: Array<unknown>
    }
}

type GTMData = {
    gtmHandler: DataLayerAction
    gtmInitScript: string
    gtmNoScript: string
}

export const useGTM = (
    mappingOverride: GTMEventDataMapping = {},
    actionOverride: GTMActionOverride = {},
    eventQueueBeforeRules = {},
    eventQueueAfterRules = {},
    delayToNextPageEvents = []
): GTMData => {
    // El snippet del contenedor se instala en `_document` (head + noscript).
    // Aquí solo el handler de dataLayer; enablement (incl. flag de local) vive
    // en resolveGtmBootstrap — misma regla que el HTML inicial.
    const { isEnabled, initScript, noScript } = resolveGtmBootstrap()

    const eventQueue = useEventQueue(
        eventQueueBeforeRules,
        eventQueueAfterRules,
        delayToNextPageEvents
    )
    const { getStoredData, addStoredData, resetStoredData } = useDataLayerData()
    const consumeEventData = getEventDataConsumer(
        eventQueue,
        getStoredData,
        addStoredData,
        resetStoredData
    )

    const applyMapping = getApplyMapping(mappingOverride)
    const handleDataLayerAction: DataLayerAction = (action) => {
        if (!isEnabled) {
            return
        }
        let mappedData = applyMapping(action)
        if (action.type && typeof actionOverride[action.type] === 'function') {
            // Data, processed by an action override does not need 'type'
            mappedData = {
                type: '',
                data: actionOverride[action.type](
                    mappedData.data,
                    addStoredData
                )
            }
        }
        consumeEventData(mappedData)
    }

    return {
        gtmHandler: handleDataLayerAction,
        gtmInitScript: initScript,
        gtmNoScript: noScript
    }
}
